import { Fragment } from 'react';
import type { MutableRefObject } from 'react';
import { Dialog, Transition } from '@headlessui/react';

type Props = {
  open: boolean;
  onClose: () => void;
  onAfterClose?: () => void;
  children: React.ReactNode;
  size?: 'md' | 'lg' | 'xl';
  initialFocus?: MutableRefObject<HTMLElement | null>;
  mobileFullScreen?: boolean;
};

const sizeClasses = {
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-4xl',
} as const;

export function Modal({
  open,
  onClose,
  onAfterClose,
  children,
  size = 'md',
  initialFocus,
  mobileFullScreen = false,
}: Props) {
  return (
    <Transition appear show={open} as={Fragment} afterLeave={onAfterClose}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={onClose}
        initialFocus={initialFocus}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div
            className={`flex min-h-full items-center justify-center text-center ${
              mobileFullScreen ? 'p-0 sm:p-4' : 'p-4'
            }`}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                data-testid="modal-panel"
                className={`w-full ${sizeClasses[size]} transform overflow-hidden bg-white text-left align-middle shadow-xl transition-all ${
                  mobileFullScreen
                    ? 'min-h-[100dvh] rounded-none p-0 sm:min-h-0 sm:rounded-2xl sm:p-6'
                    : 'rounded-2xl p-6'
                }`}
              >
                {children}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
